import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Icon, Flex, Button, Loading } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import ResizableWindow from 'components/general/ResizableWindow';
import { P } from 'components/typography';

import { TruncateTextComponent } from '../../../components/TruncateText/TruncateText';

import type { VisitorCustomFieldConfig } from 'graphql-query-contracts';

import type {
  CustomFieldsModalProps,
  CustomFieldsModalSortDirection,
  CustomFieldsModalSortField,
} from './customField.types';

import { getVisitorCustomFieldTypeDisplayLabel } from './fieldTypeDisplayLabel';
import styles from './CustomFieldsModal.scss';

const FIELDS_PER_PAGE = 5;
const MAX_FIELD_NAME_DISPLAY_LENGTH = 40;
const MAX_VISIBLE_PAGES = 3;
const MAX_CUSTOM_FIELDS_PER_FORM = 5;
const MAX_SELECTION_TOAST_KEY =
  'web.admin.content.features.visitor.management.general.customFields.maxSelectionReached';
const ELLIPSIS = '...';
const FIELD_NAME_TOOLTIP_Z_INDEX = 100;
const EMPTY_CUSTOM_FIELDS: VisitorCustomFieldConfig[] = [];
const EMPTY_FIELD_IDS: string[] = [];
const MODAL_DEFAULT_POSITION = { width: 600, left: 300, height: 500, top: 80 };

function setsAreEqual(setA: Set<string>, setB: Set<string>): boolean {
  if (setA.size !== setB.size) return false;

  for (const id of setA) {
    if (!setB.has(id)) return false;
  }

  return true;
}

function getFieldSortValue(
  field: VisitorCustomFieldConfig,
  sortField: CustomFieldsModalSortField,
  t: (key: string) => string
): string {
  return sortField === 'fieldType'
    ? getVisitorCustomFieldTypeDisplayLabel(field.fieldType, t).toLowerCase()
    : field[sortField].toLowerCase();
}

export function CustomFieldsModal({
  isOpen,
  customFields = EMPTY_CUSTOM_FIELDS,
  loading = false,
  error,
  onClose,
  onSave,
  alreadyAddedFieldIds = EMPTY_FIELD_IDS,
  hasFetched = true,
}: CustomFieldsModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<CustomFieldsModalSortField>('fieldName');
  const [sortDirection, setSortDirection] = useState<CustomFieldsModalSortDirection>('asc');
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(
    () => {
      const enabledIds = new Set(
        customFields.filter(f => f.isEnabled).map(f => f.id)
      );

      return new Set(alreadyAddedFieldIds.filter(id => enabledIds.has(id)));
    }
  );

  const alreadyAddedSet = useMemo(
    () => new Set(alreadyAddedFieldIds),
    [alreadyAddedFieldIds]
  );

  useEffect(() => {
    const enabledIds = new Set(customFields.filter(f => f.isEnabled).map(f => f.id));

    setSelectedFieldIds(new Set(alreadyAddedFieldIds.filter(id => enabledIds.has(id))));
  }, [alreadyAddedFieldIds, customFields]);

  const filteredFields = useMemo(() => {
    let result = customFields.filter(f => f.isEnabled);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      result = result.filter(
        field =>
          field.fieldName.toLowerCase().includes(query) ||
          getVisitorCustomFieldTypeDisplayLabel(field.fieldType, t).toLowerCase().includes(query)
      );
    }

    return [...result].sort((recordA, recordB) => {
      const isFieldAAlreadyAdded = alreadyAddedSet.has(recordA.id);
      const isFieldBAlreadyAdded = alreadyAddedSet.has(recordB.id);

      if (isFieldAAlreadyAdded !== isFieldBAlreadyAdded) return isFieldAAlreadyAdded ? -1 : 1;

      const sortValueComparison = getFieldSortValue(recordA, sortField, t).localeCompare(getFieldSortValue(recordB, sortField, t));

      return sortDirection === 'asc' ? sortValueComparison : -sortValueComparison;
    });
  }, [customFields, searchQuery, sortField, sortDirection, t, alreadyAddedSet]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFields.length / FIELDS_PER_PAGE)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages - 1);

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  const paginatedFields = filteredFields.slice(
    safeCurrentPage * FIELDS_PER_PAGE,
    (safeCurrentPage + 1) * FIELDS_PER_PAGE
  );

  const hasFields = filteredFields.length > 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const handleSort = (field: CustomFieldsModalSortField) => {
    setCurrentPage(0);

    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const isAllSelected =
    filteredFields.length > 0 &&
    filteredFields.every(f => selectedFieldIds.has(f.id));

  const handleToggleField = useCallback((id: string) => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_CUSTOM_FIELDS_PER_FORM) {
        next.add(id);
      } else {
        window.Toast.show(t(MAX_SELECTION_TOAST_KEY, { max: MAX_CUSTOM_FIELDS_PER_FORM }));
      }

      return next;
    });
  }, [t]);

  const handleToggleAll = useCallback(() => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);

      if (filteredFields.every(f => prev.has(f.id))) {
        for (const f of filteredFields) next.delete(f.id);
      } else {
        const unselectedCount = filteredFields.filter(f => !prev.has(f.id)).length;
        const availableSlots = MAX_CUSTOM_FIELDS_PER_FORM - prev.size;

        for (const f of filteredFields) {
          if (next.size >= MAX_CUSTOM_FIELDS_PER_FORM) break;

          next.add(f.id);
        }

        if (unselectedCount > availableSlots) {
          window.Toast.show(t(MAX_SELECTION_TOAST_KEY, { max: MAX_CUSTOM_FIELDS_PER_FORM }));
        }
      }

      return next;
    });
  }, [filteredFields, t]);

  const hasChanges = useMemo(
    () => !setsAreEqual(selectedFieldIds, alreadyAddedSet),
    [selectedFieldIds, alreadyAddedSet]
  );

  const handleSave = useCallback(() => {
    if (!onSave || !hasChanges) return;

    const selected = customFields.filter(f => selectedFieldIds.has(f.id));

    if (selected.length > MAX_CUSTOM_FIELDS_PER_FORM && selected.length >= alreadyAddedSet.size) return;

    onSave(selected);
  }, [onSave, hasChanges, selectedFieldIds, customFields, alreadyAddedSet]);

  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];

    let startPage = Math.max(
      0,
      safeCurrentPage - Math.floor(MAX_VISIBLE_PAGES / 2)
    );
    const endPage = Math.min(startPage + MAX_VISIBLE_PAGES, totalPages);

    if (endPage - startPage < MAX_VISIBLE_PAGES) {
      startPage = Math.max(0, endPage - MAX_VISIBLE_PAGES);
    }

    if (startPage > 0) {
      pages.push(
        <button
          key="first"
          type="button"
          className={styles.pageButton}
          onClick={() => setCurrentPage(0)}
        >
          {1}
        </button>
      );

      if (startPage > 1) {
        pages.push(
          <span key="ellipsis-start" className={styles.ellipsis}>
            {ELLIPSIS}
          </span>
        );
      }
    }

    for (let i = startPage; i < endPage; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          className={`${styles.pageButton} ${safeCurrentPage === i ? styles.active : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i + 1}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis-end" className={styles.ellipsis}>
            {ELLIPSIS}
          </span>
        );
      }

      pages.push(
        <button
          key="last"
          type="button"
          className={styles.pageButton}
          onClick={() => setCurrentPage(totalPages - 1)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (!isOpen) return null;

  const header = (
    <div className={styles.header} data-test="customFieldsModal">
      <span className={styles.headerTitle}>
        {t(
          'web.admin.content.features.visitor.management.general.customFields.title'
        )}
      </span>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        data-test="customFieldsModalClose"
        aria-label={t(
          'web.admin.content.features.visitor.management.general.customFields.close'
        )}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <Button
        variant="secondary"
        onClick={onClose}
        testId="customFieldsModalCancel"
        className={styles.actionButton}
      >
        {t(
          'web.admin.content.features.visitor.management.general.customFields.cancel'
        )}
      </Button>
      <Button
        variant="primary"
        disabled={!hasChanges}
        onClick={handleSave}
        testId="customFieldsModalAdd"
        className={styles.actionButton}
      >
        {t(
          'web.admin.content.features.visitor.management.general.customFields.add'
        )}
      </Button>
    </div>
  );

  return (
    <ResizableWindow
      name="customFieldsModal"
      className={styles.modal}
      contentContainerClassName={styles.contentContainer}
      lockWidth
      defaultPosition={MODAL_DEFAULT_POSITION}
      TopMenuComponent={header}
      BottomMenuComponent={footer}
    >
      <div className={styles.body}>

        {(!hasFetched || loading) && (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>
        )}

        {hasFetched && !loading && error && (
          <div
            className={styles.errorContainer}
            data-test="customFieldsError"
          >
            <Icon
              name="circle-exclamation"
              set={ICON_SET_FONTAWESOME}
              size="large"
            />
            <P>
              {t(
                'web.admin.content.features.visitor.management.general.customFields.loadError'
              )}
            </P>
          </div>
        )}

        {hasFetched && !loading && !error && (
          <>
            <div className={styles.searchAndHeaderBox}>
              <div className={styles.searchInputWrapper}>
                <Icon
                  name="search"
                  set={ICON_SET_FONTAWESOME}
                  className={styles.searchIcon}
                />
                <input
                  type="text"
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t(
                    'web.admin.content.features.visitor.management.general.customFields.searchPlaceholder'
                  )}
                  data-test="customFieldsSearchInput"
                />
              </div>
              <P className={styles.fieldCountLabel} data-test="customFieldsCount">
                {t(
                  'web.admin.content.features.visitor.management.general.customFields.selectionCount',
                  { selected: selectedFieldIds.size, max: MAX_CUSTOM_FIELDS_PER_FORM }
                )}
              </P>
            </div>

            <div className={styles.listContainer} data-test="customFieldsListContainer">
              {hasFields ? (
                <>
                  <div className={styles.fieldsContainer}>
                    <table className={styles.fieldsTable}>
                      <thead className={styles.tableHeader}>
                        <tr>
                          <th className={styles.checkboxCell}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={isAllSelected}
                              onChange={handleToggleAll}
                              disabled={filteredFields.length === 0}
                              aria-label={t(
                                'web.admin.content.features.visitor.management.general.customFields.selectAll'
                              )}
                              data-test="customFieldsSelectAll"
                            />
                          </th>
                          <th>
                            <button
                              type="button"
                              className={styles.sortButton}
                              onClick={() => handleSort('fieldName')}
                              data-test="customFieldsSortName"
                            >
                              {t(
                                'web.admin.content.features.visitor.management.general.customFields.fieldName'
                              )}
                              <Icon
                                name="sort"
                                set={ICON_SET_FONTAWESOME}
                                className={styles.sortIcon}
                              />
                            </button>
                          </th>
                          <th className={styles.fieldTypeHeader}>
                            <button
                              type="button"
                              className={styles.sortButton}
                              onClick={() => handleSort('fieldType')}
                              data-test="customFieldsSortType"
                            >
                              {t(
                                'web.admin.content.features.visitor.management.general.customFields.fieldType'
                              )}
                              <Icon
                                name="sort"
                                set={ICON_SET_FONTAWESOME}
                                className={styles.sortIcon}
                              />
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedFields.map(field => (
                          <tr
                            key={field.id}
                            className={styles.tableRow}
                            data-test="customFieldItem"
                          >
                            <td className={styles.checkboxCell}>
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={selectedFieldIds.has(field.id)}
                                onChange={() => handleToggleField(field.id)}
                                aria-label={t(
                                  'web.admin.content.features.visitor.management.general.customFields.selectField',
                                  { fieldName: field.fieldName }
                                )}
                              />
                            </td>
                            <td className={styles.fieldNameCell}>
                              {field.fieldName.length > MAX_FIELD_NAME_DISPLAY_LENGTH ? (
                                  <TruncateTextComponent
                                    text={field.fieldName}
                                    maxLength={MAX_FIELD_NAME_DISPLAY_LENGTH}
                                    className={styles.truncatedFieldName}
                                    withTooltip = {true}
                                    tooltipZIndex={FIELD_NAME_TOOLTIP_Z_INDEX}
                                  />
                              ) : (
                                <TruncateTextComponent
                                  text={field.fieldName}
                                  maxLength={MAX_FIELD_NAME_DISPLAY_LENGTH}
                                  className={styles.truncatedFieldName}
                                  withTooltip = {false}
                                />
                              )}
                            </td>
                            <td className={styles.fieldTypeCell}>
                              {getVisitorCustomFieldTypeDisplayLabel(field.fieldType, t)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  className={styles.emptyState}
                  data-test="customFieldsEmptyState"
                >
                  <Icon
                    name="list"
                    set={ICON_SET_FONTAWESOME}
                    size="large"
                    className={styles.emptyStateIcon}
                  />
                  <P className={styles.emptyStateText}>
                    {customFields.length === 0 || !searchQuery.trim()
                      ? t(
                          'web.admin.content.features.visitor.management.general.customFields.emptyState'
                        )
                      : t(
                          'web.admin.content.features.visitor.management.general.customFields.noSearchResults'
                        )}
                  </P>
                </Flex>
              )}
            </div>

            {totalPages > 1 && (
              <Flex
                align="center"
                justify="center"
                gap={2}
                className={styles.pagination}
                data-test="customFieldsPagination"
              >
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(0)}
                  disabled={safeCurrentPage === 0}
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.firstPage')}
                >
                  <Icon
                    name="chevron-double-left"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={safeCurrentPage === 0}
                  data-test="customFieldsPaginationPrev"
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.previousPage')}
                >
                  <Icon
                    name="chevron-left"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>

                {renderPageNumbers()}

                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={safeCurrentPage === totalPages - 1}
                  data-test="customFieldsPaginationNext"
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.nextPage')}
                >
                  <Icon
                    name="chevron-right"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={safeCurrentPage === totalPages - 1}
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.lastPage')}
                >
                  <Icon
                    name="chevron-double-right"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
              </Flex>
            )}
          </>
        )}
      </div>
    </ResizableWindow>
  );
}
